import { forwardRef, memo, useCallback, useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';

import { DEFAULT_MONTHS_COUNT } from '@calendar/calendarHelpers';
import Day from '@calendar/components/Day';
import useHandleMonthChange from '@calendar/hooks/useHandleMonthChange';
import { MonthToRender } from '@calendar/types/MonthsToRender';
import { calculateThreshold } from '@utils/calculateThreshold';
import { formatCsMonth } from '@utils/formatCs';

interface MonthProps {
  renderMountTitle: boolean;
  monthToRender: MonthToRender;
}

const Month = (
  { renderMountTitle, monthToRender }: MonthProps,
  ref?: React.ForwardedRef<HTMLDivElement>,
) => {
  const handleMonthChange = useHandleMonthChange();

  const [threshold, setThreshold] = useState(
    calculateThreshold(window.innerHeight),
  );
  const { ref: inViewRef, inView } = useInView({
    threshold,
    initialInView: monthToRender.days.length < 7,
    fallbackInView: true,
  });

  useEffect(() => {
    const handleResize = () =>
      setThreshold(calculateThreshold(window.innerHeight));

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (inView && monthToRender.index <= DEFAULT_MONTHS_COUNT) {
      handleMonthChange(monthToRender.index + 1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, monthToRender.index]);

  const setRefs = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        // eslint-disable-next-line no-param-reassign
        (ref as React.MutableRefObject<HTMLDivElement>).current = node;
        inViewRef(node);
      }
    },
    [inViewRef, ref],
  );

  return (
    <div className="flex-col">
      {monthToRender.isRendered && (
        <>
          {renderMountTitle && (
            <h3 className="mb-4 ml-[80px] mt-8 font-montserrat text-[24px]/[34px] font-semibold text-brand-gray-100">
              {formatCsMonth(monthToRender.month)}
            </h3>
          )}
          <div ref={setRefs} className="scroll-my-52 lg:scroll-my-32">
            {monthToRender.days.map((day: Date) => (
              <Day key={Number(day)} date={day} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(forwardRef(Month), (prevProps, nextProps) => {
  const isMonthToRenderEqual =
    prevProps.monthToRender.index === nextProps.monthToRender.index &&
    prevProps.monthToRender.isRendered === nextProps.monthToRender.isRendered;

  return isMonthToRenderEqual;
});
